import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionContext } from '@supabase/auth-helpers-react';

import PasswordSecurityFeedback from '../../components/forms/PasswordSecurityFeedback';
import { supabase } from '../../supabase';
import { obtenerMensajeErrorAuth } from '../../utils/authMessages';
import { evaluarPassword, validarNuevaPassword } from '../../utils/passwordSecurity';

import {
  Alert,
  Button,
  Card,
  Container,
  FloatingLabel,
  Form,
  Spinner,
} from 'react-bootstrap';

export default function RecuperarPasswordPage() {
  const { session, isLoading: sessionLoading } = useSessionContext();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mostrarPass, setMostrarPass] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const redirectTimerRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => () => {
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
    }
  }, []);

  const passwordSecurity = useMemo(
    () => evaluarPassword(newPassword),
    [newPassword]
  );

  const {
    checks: passwordChecks,
    strength: strengthData,
    isValid: passwordIsValid,
  } = passwordSecurity;

  const passwordsMatch =
    newPassword.length > 0 &&
    confirmPassword.length > 0 &&
    newPassword === confirmPassword;

  const canSubmit =
    Boolean(session) &&
    passwordIsValid &&
    passwordsMatch &&
    !loading &&
    !completed;

  const limpiarResultado = () => {
    if (result && !completed) setResult(null);
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();

    if (loading || completed) return;

    if (!session) {
      setResult({
        variant: 'danger',
        message: 'El enlace de recuperación no es válido o ya venció. Solicitá uno nuevo.',
      });
      return;
    }

    const validationError = validarNuevaPassword(
      newPassword,
      confirmPassword
    );

    if (validationError) {
      setResult({
        variant: 'danger',
        message: validationError,
      });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      setCompleted(true);
      setResult({
        variant: 'success',
        message: 'Contraseña actualizada correctamente. Redirigiendo...',
      });

      redirectTimerRef.current = setTimeout(
        () => navigate('/servicios', { replace: true }),
        1800
      );
    } catch (error) {
      setResult({
        variant: 'danger',
        message: obtenerMensajeErrorAuth(
          error,
          'No fue posible actualizar la contraseña. Solicitá un nuevo enlace e intentá nuevamente.'
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  if (sessionLoading) {
    return (
      <Container
        fluid
        className='recover-page d-flex align-items-center justify-content-center'
      >
        <Card className='recover-card'>
          <Card.Body className='text-center'>
            <Spinner animation='border' variant='success' />
            <div className='mt-3 text-muted'>Validando enlace...</div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (!session) {
    return (
      <Container
        fluid
        className='recover-page d-flex align-items-center justify-content-center'
      >
        <Card className='recover-card'>
          <Card.Body>
            <div className='login-header'>
              <h3>Restablecer contraseña</h3>
              <div className='login-title-divider' />
            </div>

            <Alert variant='warning' className='mb-3 text-center'>
              El enlace de recuperación no es válido o ya venció. Solicitá un nuevo correo desde la pantalla de ingreso.
            </Alert>

            <Button
              type='button'
              variant='primary'
              className='login-submit'
              onClick={() => navigate('/login', { replace: true })}
            >
              Volver al ingreso
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container
      fluid
      className='recover-page d-flex align-items-center justify-content-center'
    >
      <Card className='recover-card'>
        <Card.Body>
          <div className='login-header'>
            <h3>Restablecer contraseña</h3>
            <div className='login-title-divider' />
          </div>

          <div className='recover-info'>
            Elegí una nueva contraseña para volver a acceder a tu cuenta de forma segura.
          </div>

          <Form onSubmit={handleChangePassword} className='login-form mt-4'>
            <FloatingLabel controlId='newPassword' label='Nueva contraseña'>
              <Form.Control
                type={mostrarPass ? 'text' : 'password'}
                value={newPassword}
                onChange={(event) => {
                  setNewPassword(event.target.value);
                  limpiarResultado();
                }}
                placeholder='Nueva contraseña'
                autoComplete='new-password'
                required
                disabled={loading || completed}
              />
            </FloatingLabel>

            <PasswordSecurityFeedback
              checks={passwordChecks}
              strength={strengthData}
            />

            <FloatingLabel
              controlId='confirmPassword'
              label='Confirmar contraseña'
            >
              <Form.Control
                type={mostrarPass ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(event) => {
                  setConfirmPassword(event.target.value);
                  limpiarResultado();
                }}
                placeholder='Confirmar contraseña'
                autoComplete='new-password'
                required
                disabled={loading || completed}
              />
            </FloatingLabel>

            {confirmPassword && (
              <div className={`password-match ${passwordsMatch ? 'is-valid' : ''}`}>
                {passwordsMatch
                  ? 'Las contraseñas coinciden.'
                  : 'Las contraseñas no coinciden.'}
              </div>
            )}

            <Form.Check
              type='checkbox'
              label='Mostrar contraseña'
              checked={mostrarPass}
              onChange={(event) => setMostrarPass(event.target.checked)}
              disabled={loading || completed}
            />

            <Button
              type='submit'
              variant='primary'
              className='login-submit'
              disabled={!canSubmit}
            >
              {loading ? (
                <>
                  <Spinner animation='border' size='sm' className='me-2' />
                  Actualizando...
                </>
              ) : completed ? (
                'Contraseña actualizada'
              ) : (
                'Actualizar contraseña'
              )}
            </Button>
          </Form>

          {result && (
            <Alert
              variant={result.variant}
              className='mt-4 mb-0 text-center'
              role='status'
              aria-live='polite'
            >
              {result.message}
            </Alert>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
